import * as React from 'react';
import PropTypes from 'prop-types';
import {SxProps, Theme} from '@mui/material/styles';
import {
  GridColDef,
  GridFilterForm,
  GridFilterFormProps,
  GridFilterItem,
  gridFilterModelSelector,
  GridLogicOperator,
  GridPanelContent,
  GridPanelFooter,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import {
  gridFilterableColumnDefinitionsSelector,
  gridFilterableColumnLookupSelector,
  GridStateColDef,
  useGridSelector,
} from '@mui/x-data-grid/internals';
import {t} from 'i18next';
import AurButton from '../common/aur-button/AurButton';
import {generateRandomNumber} from '../../utils/utils';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../constants/analyticsConstants';
import {useAnalytics} from '../../hooks/useAnalytics';

interface GetColumnForNewFilterArgs {
  currentFilters: GridFilterItem[];
  columns: GridStateColDef[];
}

interface GridFilterPanelProps extends Pick<
  GridFilterFormProps,
  'logicOperators' | 'columnsSort'
> {
  onSetFilterSource?: () => void;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps<Theme>;
  /**
   * Function that returns the next filter item to be picked as default filter.
   * @param {GetColumnForNewFilterArgs} args Currently configured filters and columns.
   * @returns {GridColDef['field']} The field to be used for the next filter or `null` to prevent adding a filter.
   */
  getColumnForNewFilter?: (
    args: GetColumnForNewFilterArgs
  ) => GridColDef['field'] | null;
  /**
   * Props passed to each filter form.
   */
  filterFormProps?: Pick<
    GridFilterFormProps,
    | 'columnsSort'
    | 'deleteIconProps'
    | 'logicOperatorInputProps'
    | 'operatorInputProps'
    | 'columnInputProps'
    | 'valueInputProps'
    | 'filterColumns'
  >;

  /**
   * If `true`, the `Add filter` button will not be displayed.
   * @default false
   */
  disableAddFilterButton?: boolean;
  /**
   * If `true`, the `Remove all` button will be disabled
   * @default false
   */
  disableRemoveAllButton?: boolean;
  /**
   * @ignore - do not document.
   */
  children?: React.ReactNode;
  currentColumn: string;
  loader: boolean;
}

const getGridFilter = (col: GridStateColDef): GridFilterItem => ({
  field: col.field,
  operator: col.filterOperators?.[0]?.value ?? '',
  id: Math.round(generateRandomNumber() * 1e5),
});

const CustomGridFilterPanel = React.forwardRef<
  HTMLDivElement,
  GridFilterPanelProps
>(function GridFilterPanel(props, ref) {
  const apiRef = useGridApiContext();
  const {trackGAEvent} = useAnalytics();

  const rootProps = useGridRootProps();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterableColumns = useGridSelector(
    apiRef,
    gridFilterableColumnDefinitionsSelector
  );
  const filterableColumnsLookup = useGridSelector(
    apiRef,
    gridFilterableColumnLookupSelector
  );
  const lastFilterRef = React.useRef<HTMLInputElement>(null);

  const {
    logicOperators = [GridLogicOperator.And, GridLogicOperator.Or],
    columnsSort,
    filterFormProps,
    disableAddFilterButton = false,
    disableRemoveAllButton = false,
    currentColumn,
    loader = false,
    ...other
  } = props;

  // Initialize local state with current filterModel.items or a default filter
  const [localFilterItems, setLocalFilterItems] = React.useState<
    GridFilterItem[]
  >(() => {
    if (filterModel.items.length) {
      return filterModel.items;
    }
    const nextColumnWithOperator = filterableColumns.find(
      (colDef) =>
        colDef.filterOperators?.length && currentColumn === colDef.field
    );
    return nextColumnWithOperator
      ? [getGridFilter(nextColumnWithOperator)]
      : [];
  });

  const getDefaultFilter = React.useCallback((): GridFilterItem | null => {
    const nextColumnWithOperator = filterableColumns.find(
      (colDef) =>
        colDef.filterOperators?.length && currentColumn === colDef.field
    );
    return nextColumnWithOperator
      ? getGridFilter(nextColumnWithOperator)
      : null;
  }, [currentColumn, filterableColumns]);

  // Compute valid and read-only filters based on localFilterItems
  const {readOnlyFilters, validFilters} = React.useMemo<{
    readOnlyFilters: GridFilterItem[];
    validFilters: GridFilterItem[];
  }>(
    () =>
      localFilterItems.reduce(
        (acc, item) => {
          if (
            filterableColumnsLookup[item.field] &&
            currentColumn === item.field
          ) {
            acc.validFilters.push(item);
          } else {
            acc.readOnlyFilters.push(item);
          }
          return acc;
        },
        {
          readOnlyFilters: [] as GridFilterItem[],
          validFilters: [] as GridFilterItem[],
        }
      ),
    [localFilterItems, filterableColumnsLookup, currentColumn]
  );

  const addNewFilter = React.useCallback(() => {
    const newFilter = getDefaultFilter();
    if (!newFilter) return;
    setLocalFilterItems((prev) => [...prev, newFilter]);
  }, [getDefaultFilter]);

  const deleteFilter = React.useCallback(
    (item: GridFilterItem) => {
      const shouldCloseFilterPanel = validFilters.length === 1;
      setLocalFilterItems((prev) =>
        prev.filter((filter) => filter.id !== item.id)
      );
      if (shouldCloseFilterPanel) {
        apiRef.current.hideFilterPanel();
      }
    },
    [apiRef, validFilters.length]
  );

  const handleFilterChange = React.useCallback(
    (updatedItem: GridFilterItem) => {
      setLocalFilterItems((prev) =>
        prev.map((item) =>
          item.id === updatedItem.id ? {...updatedItem} : item
        )
      );
    },
    []
  );

  const handleRemoveAll = React.useCallback(() => {
    const updatedFilterItems = filterModel.items.filter(
      (item) => item.field !== currentColumn
    );
    setLocalFilterItems([]);
    apiRef.current.setFilterModel({
      ...filterModel,
      items: updatedFilterItems,
    });
    apiRef.current.hideFilterPanel();
  }, [apiRef, filterModel, currentColumn]);

  const handleApplyFilters = React.useCallback(() => {
    const activeItem = localFilterItems.find(
      (item) => item.field === currentColumn
    );

    if (
      activeItem &&
      activeItem.value !== undefined &&
      activeItem.value !== null &&
      activeItem.value !== ''
    ) {
      // 1. Track the ADVANCE event
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILTER,
        click: {
          name: activeItem.field,
          region: GA_REGIONS.BODY,
          type: GA_CLICK_TYPES.ICON,
          action: t('gaTracking.ecaDocList.filterAdvance'),
        },
      });

      // 2. Set the flag so the main component ignores this update
      props.onSetFilterSource?.();
    }
    apiRef.current.setFilterModel({
      ...filterModel,
      items: localFilterItems.filter(
        (item) =>
          item.value !== undefined && item.value !== null && item.value !== ''
      ),
    });
    apiRef.current.hideFilterPanel();
  }, [apiRef, filterModel, localFilterItems]);

  React.useEffect(() => {
    if (
      logicOperators.length > 0 &&
      filterModel.logicOperator &&
      !logicOperators.includes(filterModel.logicOperator)
    ) {
      apiRef.current.setFilterLogicOperator(logicOperators[0]);
    }
  }, [logicOperators, filterModel.logicOperator]);

  React.useEffect(() => {
    if (validFilters.length === 0 && !loader) {
      addNewFilter();
    }
  }, [validFilters.length, addNewFilter, loader]);

  React.useEffect(() => {
    if (validFilters.length > 0) {
      lastFilterRef.current?.focus();
    }
  }, [validFilters.length]);

  const isAddFilterDisabled =
    loader || validFilters.some((item) => !item.value && item.value !== 0);

  return (
    <div
      {...other}
      ref={ref}
      className={`eca-doc-list-filter-panel ${loader ? 'disableFilter' : ''}`}
    >
      <GridPanelContent>
        {validFilters.map((item, index) => (
          <GridFilterForm
            key={item.id ?? index + readOnlyFilters.length}
            item={item}
            applyFilterChanges={handleFilterChange}
            deleteFilter={deleteFilter}
            hasMultipleFilters={localFilterItems.length > 1}
            showMultiFilterOperators={readOnlyFilters.length + index > 0}
            disableMultiFilterOperator={readOnlyFilters.length + index !== 1}
            applyMultiFilterOperatorChanges={(operator) =>
              apiRef.current.setFilterLogicOperator(operator)
            }
            focusElementRef={
              index === validFilters.length - 1 ? lastFilterRef : null
            }
            logicOperators={logicOperators}
            columnsSort={columnsSort}
            readOnly={loader}
            {...filterFormProps}
          />
        ))}
      </GridPanelContent>
      {!rootProps.disableMultipleColumnsFiltering &&
        !(disableAddFilterButton && disableRemoveAllButton) && (
          <GridPanelFooter>
            <div className="footer-btn-grp">
              {!disableAddFilterButton && (
                <rootProps.slots.baseButton
                  onClick={addNewFilter}
                  startIcon={<rootProps.slots.filterPanelAddIcon aria-hidden="true" />}
                  disabled={isAddFilterDisabled}
                  className={`${isAddFilterDisabled ? 'disableFilter' : ''}`}
                  {...rootProps.slotProps?.baseButton}
                >
                  {apiRef.current.getLocaleText('filterPanelAddFilter')}
                </rootProps.slots.baseButton>
              )}
              {!disableRemoveAllButton && validFilters.length > 0 && (
                <rootProps.slots.baseButton
                  onClick={handleRemoveAll}
                  startIcon={<rootProps.slots.filterPanelRemoveAllIcon aria-hidden="true" />}
                  disabled={loader}
                  {...rootProps.slotProps?.baseButton}
                >
                  {apiRef.current.getLocaleText('filterPanelRemoveAll')}
                </rootProps.slots.baseButton>
              )}
            </div>
            <AurButton
              classNames="aur-date-apply-button"
              type="continue"
              title={t('common.aurDatePicker.apply')}
              label={t('common.aurDatePicker.apply')}
              size="Medium"
              onClick={handleApplyFilters}
              dataTestId="Apply-button"
              disabled={loader || isAddFilterDisabled}
            />
          </GridPanelFooter>
        )}
    </div>
  );
});

CustomGridFilterPanel.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * @ignore - do not document.
   */
  children: PropTypes.node,
  /**
   * Changes how the options in the columns selector should be ordered.
   * If not specified, the order is derived from the `columns` prop.
   */
  columnsSort: PropTypes.oneOf(['asc', 'desc']),
  /**
   * If `true`, the `Add filter` button will not be displayed.
   * @default false
   */
  disableAddFilterButton: PropTypes.bool,
  /**
   * If `true`, the `Remove all` button will be disabled
   * @default false
   */
  disableRemoveAllButton: PropTypes.bool,
  /**
   * Props passed to each filter form.
   */
  filterFormProps: PropTypes.shape({
    columnInputProps: PropTypes.any,
    columnsSort: PropTypes.oneOf(['asc', 'desc']),
    deleteIconProps: PropTypes.any,
    filterColumns: PropTypes.func,
    logicOperatorInputProps: PropTypes.any,
    operatorInputProps: PropTypes.any,
    valueInputProps: PropTypes.any,
  }),
  /**
   * Function that returns the next filter item to be picked as default filter.
   * @param {GetColumnForNewFilterArgs} args Currently configured filters and columns.
   * @returns {GridColDef['field']} The field to be used for the next filter or `null` to prevent adding a filter.
   */
  getColumnForNewFilter: PropTypes.func,
  /**
   * Sets the available logic operators.
   * @default [GridLogicOperator.And, GridLogicOperator.Or]
   */
  logicOperators: PropTypes.arrayOf(PropTypes.oneOf(['and', 'or']).isRequired),
  /**
   * get the current column for which the panel is opened
   */
  currentColumn: PropTypes.any,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
} as any;

/**
 * Demos:
 * - [Filtering - overview](https://mui.com/x/react-data-grid/filtering/)
 *
 * API:
 * - [GridFilterPanel API](https://mui.com/x/api/data-grid/grid-filter-panel/)
 */
export {CustomGridFilterPanel, getGridFilter};
